import ListFilters from '@/common/components/ListFilters';
import Table from '@/common/components/Table';
import TextField from '@/common/components/TextField';
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker';
import useDebounce from '@/common/hooks/useDebounce';
import { useFilterDate } from '@/common/hooks/useFilterDate';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { Mapper } from '@/common/services/mapper';
import { SearchIcon } from 'lucide-react';
import { useRecipientMessages } from '@/common/hooks/queries/useRecipientMessages';
import { RecipientMessagesData, RecipientMessageTableItem } from '../../types/recipientMessages.type';
import { providerKlassOptions } from '../../constants/providerKlass';
import { recipientMessagesCompanyDefaultColumns } from '../../constants/recipientMessagesCompaniesDefaultColumns.constant';
import { CompaniesTransformer } from '@/features/companies/services/companiesTransformer';
import { useCompanies } from '@/features/companies/hooks/useCompanies';
import { RecipientMessagesCompaniesTransformer } from '../../services/recipientMessagesCompaniesTransformer';
import { recipientMessagesStatusOptions } from '../../constants/recipientMessagesStatus';

export default function RecipientMessagesCompaniesList() {
  const { filterParams, setFilterParams } = useFilterParams();

  const { date, formattedDate } = useFilterDate(filterParams);

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchCompany, setSearchCompany] = useDebounce('');

  const { data: recipientMessages, meta: metaRecipientMessages, isLoading: recipientMessagesIsLoading } = useRecipientMessages({
    filters: {
      page: filterParams.page || 1,
      search,
      clientCompanyId: filterParams.company?.value,
      providerKlass: filterParams.providerKlass?.value,
      recipientType: 'Client::User',
      sentAtGt: formattedDate.from,
      sentAtLt: formattedDate.to,
      status: filterParams.status?.value
    },
    mapper: Mapper.mapWithTransformer<RecipientMessagesData[], RecipientMessageTableItem[]>({ transformer: RecipientMessagesCompaniesTransformer.tableTransformer() })
  })

  const { data: companiesOptions } = useCompanies({
    filters: { search: searchCompany },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  return (
    <div className="h-[calc((100vh-100px-4rem-1.5rem))] flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TextField
          name="search"
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          onChange={(e) => {
            setSearch((e.target as HTMLInputElement).value);
            setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
          }}
          value={filterParams.search}
        />
        <div className="flex items-center gap-2">
          <DatePickerWithRange date={date} setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))} />

          <ListFilters
            filters={[
              {
                className: '',
                onInputChange: (inputValue) => setSearchCompany(inputValue),
                onChange: (e) => setFilterParams((prev) => ({ ...prev, company: e, page: 1 })),
                value: filterParams.company,
                options: [{ label: 'Todos', value: '' }, ...companiesOptions || []],
                placeholder: 'Empresa',
                dataTestid: 'company'
              },
              {
                className: '',
                onChange: (e) => setFilterParams((prev) => ({ ...prev, providerKlass: e })),
                value: filterParams.providerKlass,
                options: [{ label: 'Todos', value: '' }, ...providerKlassOptions || []],
                placeholder: 'Método',
                dataTestid: 'method'
              },
              {
                className: '',
                onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                value: filterParams.status,
                options: [{ label: 'Todos', value: '' }, ...recipientMessagesStatusOptions || []],
                placeholder: 'Status',
                dataTestid: 'status'
              },
            ]}
          />
        </div>
      </div>

      <Table
        columns={recipientMessagesCompanyDefaultColumns}
        isLoadingData={recipientMessagesIsLoading}
        page={filterParams.page || 1}
        rows={recipientMessages || []}
        totalPages={metaRecipientMessages.total_pages}
        totalCount={metaRecipientMessages.total}
        hasPagination
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
      />
    </div>
  )
}
